import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StateService } from '@uirouter/angular';
import { AccessDeniedService } from '../services/core.access-denied.service';

/**
 * Global handler for the backend's refusal codes.
 *
 * The backend contract (see docs/PERMISSION-REDIRECT.md) is that 401 means "log in" and
 * 403 means "you can't". Both land here; where the user ends up is AccessDeniedService's
 * decision, so the interceptor, the ui-router error hook and the route resolves agree.
 */
@Injectable()
export class HttpUnauthorizedInterceptor implements HttpInterceptor {

  constructor(
    private stateService: StateService,
    private accessDeniedService: AccessDeniedService
  ) {}

  /**
   * URLs whose refusal is an answer rather than a navigation failure: session probes
   * (which would recurse), permission probes, and the endpoints whose own resolve
   * already redirects.
   */
  private checkWhitelistedUrls(url: string): boolean {
    const regexes = [
      /\/api\/project\/.*\/(details|activity)/i,
      /\/api\/user\/.*\/permissions/i,
      /\/api\/user\/.*\/preference/i,
      /\/api\/usersession/i,
      /\/api\/poll/i,
      /\/api\/monitor/i,
      /\/api\/filesystem\/.*\/thumbnail/i,
      // Page chrome, not the page: `projectMenu()` catches its failure and returns {} on
      // purpose, so a 403 here must not abort the navigation it decorates. Note this list
      // is not limited to /api/ — any endpoint whose refusal a caller answers as data
      // belongs here.
      /\/site\/mega\//i,
    ];

    for (let i = 0; i < regexes.length; i++) {
        if (regexes[i].test(url)) {
            return true;
        }
    }

    return false;
  }

  /**
   * A 403 is only a navigation failure while a transition is in flight. In-page XHR that
   * answers 403 as *data* — a join request, a merge check, a username that is taken —
   * belongs to the component that made it, and must not bounce the user out of the page.
   */
  private shouldHandleForbidden(url: string): boolean {
    if (!this.stateService.transition) return false;
    return !this.checkWhitelistedUrls(url);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.checkWhitelistedUrls(req.url)) {
            this.accessDeniedService.checkExpiredSession(this.stateService.transition);
        }

        if (error.status === 403 && this.shouldHandleForbidden(req.url)) {
            this.accessDeniedService.goRefused(this.stateService.transition);
        }

        if (error.status === 422) {
          this.stateService.go('home.license'); // Navigate to your "bad license" route
        }

        throw error;
      })
    );
  }
}
